import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Draggable } from "react-beautiful-dnd";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faArrowCircleRight,
  faQuestion,
  faCheckCircle,
  faTimes,
  faSearch,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import {
  markAsUnreadMsg,
  markAsDuplicateMsg,
  addToFavouritesMsg,
  removeFromFavouritesMsg,
} from "../../Kanban/update";
import ActionsCog from "../general/ActionsCog";
import {
  CandidateCardLink,
  CandidateCardContainer,
  CandidateCardWrapper,
  CandidateCardTitle,
  CandidateCardContent,
  CandidateCardContentAbout,
  CandidateCardContentDate,
  CandidateStatusBadge,
  MakeOfferButton,
} from "./styles/candidate-card-styles";

library.add(
  faQuestionCircle,
  faQuestion,
  faCheckCircle,
  faTimes,
  faSearch,
  faArrowCircleRight
);

class CandidateCard extends Component {
  markAsUnread = (e) => {
    e.preventDefault();
    const { dispatch, id, unreadLink } = this.props;
    dispatch(markAsUnreadMsg({ id, unreadLink }));
  };

  markAsDuplicate = (event) => {
    event.preventDefault();
    const { dispatch, id, duplicateLink } = this.props;
    dispatch(markAsDuplicateMsg({ id, duplicateLink }));
  };

  addToFavourites = (event) => {
    event.preventDefault();
    const { dispatch, id, createActionLink } = this.props;
    dispatch(addToFavouritesMsg({ id, createActionLink }));
  };

  removeFromFavourites = (event) => {
    event.preventDefault();
    const { dispatch, id, createActionLink } = this.props;
    dispatch(removeFromFavouritesMsg({ id, createActionLink }));
  };

  showCog = (column, isRecruiter) =>
    column === "submitted-candidates" && !isRecruiter;

  showMakeOffer = (status, isRecruiter) => {
    const acceptedStatus = status === "accepted";
    return acceptedStatus && !isRecruiter;
  };

  handleStatusBadgeClick = (
    event,
    id,
    candidate,
    updateStatusLink,
    column,
    isRecruiter,
    status
  ) => {
    const { handleJobOffer, handleRecruiterConfirmation } = this.props;

    event.preventDefault();

    if (isRecruiter && status === "new-update") {
      handleRecruiterConfirmation(id, candidate, updateStatusLink, column);
    }
    if (isRecruiter && status === "offered") {
      handleJobOffer(id, candidate, updateStatusLink);
    }
  };

  cogActions = ({ unread, favourited }) => {
    if (favourited) {
      return [
        {
          name: "Mark as Duplicate",
          id: 1,
          clickAction: this.markAsDuplicate,
        },
        {
          name: "Remove from Favourites",
          id: 2,
          clickAction: this.removeFromFavourites,
        },
      ];
    }
    if (unread) {
      return [
        {
          name: "Mark as Duplicate",
          id: 1,
          clickAction: this.markAsDuplicate,
        },
        {
          name: "Add to Favourites",
          id: 2,
          clickAction: this.addToFavourites,
        },
      ];
    }
    return [
      {
        name: "Mark as Duplicate",
        id: 1,
        clickAction: this.markAsDuplicate,
      },
      {
        name: "Add to Favourites",
        id: 2,
        clickAction: this.addToFavourites,
      },
      {
        name: "Mark as Unread",
        id: 3,
        clickAction: this.markAsUnread,
      },
    ];
  };

  handleDragDisabledCards = (status, column) => {
    const disabledStatuses = ["successful", "unsuccessful", "new-update"];
    const disabledColumns = ["rejected-candidates"];

    return (
      disabledStatuses.includes(status) || disabledColumns.includes(column)
    );
  };

  render() {
    const {
      added,
      agent,
      candidate,
      candidateUpdates,
      column,
      handleMakeOffer,
      id,
      index,
      isRecruiter,
      link,
      status,
      tooltip,
      unread,
      updateStatusLink,
    } = this.props;

    const favourited = status === "added_to_favourites" && !isRecruiter;

    return (
      <Draggable
        draggableId={id}
        index={index}
        key={id}
        isDragDisabled={this.handleDragDisabledCards(status, column)}
      >
        {(provided, snapshot) => (
          <CandidateCardContainer
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            data-test-id="candidate-card"
            className={`kanban-card unread-${unread} favourited-${favourited} ${status} ${column}`}
          >
            <CandidateCardLink
              href={link}
              title="Candidate"
              className="kanban-candidate-link"
              data-test-id="candidate-info-link"
              isDragging={snapshot.isDragging}
            >
              <CandidateCardWrapper className="kanban-card-wrapper">
                {this.showMakeOffer(status, isRecruiter) && (
                  <Fragment>
                    <ReactTooltip
                      place="bottom"
                      type="dark"
                      effect="solid"
                      aria-haspopup="true"
                      id="make-offer-tooltip"
                    />
                    <MakeOfferButton
                      data-tip="Make a job offer"
                      data-for="make-offer-tooltip"
                      onClick={(e) =>
                        handleMakeOffer(e, id, candidate, updateStatusLink)
                      }
                    >
                      <FontAwesomeIcon icon={faHandshake} />
                    </MakeOfferButton>
                  </Fragment>
                )}
                <CandidateCardTitle
                  className="card-title"
                  isDragging={snapshot.isDragging}
                >
                  {candidate}
                </CandidateCardTitle>
                {this.showCog(column, isRecruiter) && (
                  <ActionsCog
                    actions={this.cogActions({
                      unread,
                      favourited,
                    })}
                    isDragging={snapshot.isDragging}
                  />
                )}
                <CandidateCardContent className="card-content">
                  <CandidateCardContentAbout isDragging={snapshot.isDragging}>
                    Agent: {agent}
                  </CandidateCardContentAbout>
                  <CandidateCardContentDate isDragging={snapshot.isDragging}>
                    Added: {added}
                  </CandidateCardContentDate>
                  <CandidateStatusBadge
                    isDragging={snapshot.isDragging}
                    data-test-id="candidate-status-badge"
                    onClick={(e) => {
                      this.handleStatusBadgeClick(
                        e,
                        id,
                        candidate,
                        updateStatusLink,
                        column,
                        isRecruiter,
                        status
                      );
                    }}
                  >
                    <ReactTooltip
                      place="bottom"
                      type="dark"
                      effect="solid"
                      aria-haspopup="true"
                      id="update-tooltip"
                    />
                    <FontAwesomeIcon
                      icon={candidateUpdates}
                      data-tip={tooltip}
                      data-for="update-tooltip"
                      data-test-id="update-tooltip-icon"
                    />
                  </CandidateStatusBadge>
                </CandidateCardContent>
              </CandidateCardWrapper>
            </CandidateCardLink>
          </CandidateCardContainer>
        )}
      </Draggable>
    );
  }
}

CandidateCard.propTypes = {
  added: PropTypes.string.isRequired,
  agent: PropTypes.string.isRequired,
  candidate: PropTypes.string.isRequired,
  candidateUpdates: PropTypes.string.isRequired,
  column: PropTypes.string,
  createActionLink: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  duplicateLink: PropTypes.string,
  handleJobOffer: PropTypes.func.isRequired,
  handleMakeOffer: PropTypes.func.isRequired,
  handleRecruiterConfirmation: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  isRecruiter: PropTypes.bool,
  link: PropTypes.string.isRequired,
  status: PropTypes.string,
  tooltip: PropTypes.string.isRequired,
  unread: PropTypes.bool,
  unreadLink: PropTypes.string,
  updateStatusLink: PropTypes.string,
};

CandidateCard.defaultProps = {
  column: "",
  duplicateLink: PropTypes.isNull,
  isRecruiter: PropTypes.bool.isNull,
  status: PropTypes.isNull,
  unread: PropTypes.isFalse,
  unreadLink: PropTypes.isNull,
  updateStatusLink: PropTypes.isNull,
};

export default CandidateCard;
